import { useCallback, useState } from 'react';

import AttestationModal from '../users/AttestationModal';
import { needsAttestation } from '../../services/mfa';
import { normalizeId } from '../../utils/ids';

/**
 * A multi add that writes a card asking for a SIGNATURE (`record.attest`,
 * KPR-1725). The API asks for it once, before writing anything: 428
 * `attestation_required` naming every signing card the submit writes
 * (`card_ids`). The page opens the signature dialog ONCE — one PIN for the
 * whole submit, a med pass's every dose — and resends the same body with it.
 * Cancel writes nothing (the page keeps every value); a signature the API
 * then refuses is said, never asked again in a loop.
 */

/** Why a pending submit stopped: the person closed the signature dialog. */
export const NOT_SIGNED = Symbol('not-signed');

/** The signing cards a 428 names: every one (`card_ids`), else its one `card_id`. */
export const signingCardIds = (data) => {
	const list = Array.isArray(data?.card_ids) ? data.card_ids.map(normalizeId).filter(Boolean) : [];
	if (list.length) return list;
	const one = normalizeId(data?.card_id);
	return one ? [one] : [];
};

/** How many items of the submit land on these cards: the steps that are on, their lines. */
export function itemsOnCards(form, included, values, cardIds) {
	const signing = new Set(cardIds);
	return (form?.steps || []).reduce((n, step) => (
		included?.[step.key] && signing.has(normalizeId(step.card_id)) ? n + (values?.steps?.[step.key] || []).length : n
	), 0);
}

/**
 * `{ withSignature, refusalOf, dialog }`:
 * - `withSignature(send, countFor)` runs `send()` (a function of the request
 *   config); on the 428 it opens the dialog and resends with the signature.
 *   `countFor(cardIds)` says how many records the signature goes on.
 * - `refusalOf(error)` → `{ level, text }` for a stop the signature caused
 *   (closed: info; refused after signing: error), else null.
 * - `dialog` is the dialog, mounted only while a submit waits on it.
 */
export default function useMultiAddSignature({ collectionId }) {
	const [pending, setPending] = useState(null);

	const withSignature = useCallback((send, countFor) => send().catch(error => {
		if (!needsAttestation(error)) throw error;
		const data = error?.response?.data || {};
		const cardIds = signingCardIds(data);
		// A 428 that names no card is never answered with a signature held to
		// no card: it is said as the error it is.
		if (!cardIds.length) throw error;
		return new Promise((resolve, reject) => {
			// Settled once: Sign, then the dialog's own close, must not turn a
			// signed submit into a cancelled one.
			let settled = false;
			const once = fn => (arg) => {
				if (settled) return;
				settled = true;
				setPending(null);
				fn(arg);
			};
			setPending({
				mode: data.mode === 'password' ? 'password' : 'pin',
				cardIds,
				count: (typeof countFor === 'function' && countFor(cardIds)) || 1,
				onSigned: once(token => send({ headers: { 'X-Attestation': token } }).then(resolve, reject)),
				onCancel: once(() => reject(NOT_SIGNED)),
			});
		});
	}), []);

	const refusalOf = useCallback((error) => {
		if (error === NOT_SIGNED) {
			return { level: 'info', text: 'Not added — these items are added once you sign. Nothing was written, and what you entered is still here.' };
		}
		if (needsAttestation(error)) {
			return { level: 'error', text: `${error?.response?.data?.message || 'The signature was not accepted.'} Nothing was added.` };
		}
		return null;
	}, []);

	const dialog = pending ? (
		<AttestationModal
			open
			mode={pending.mode}
			collectionId={collectionId}
			cardId={pending.cardIds[0]}
			cardIds={pending.cardIds}
			count={pending.count}
			onSigned={token => pending.onSigned(token)}
			onClose={() => pending.onCancel()}
		/>
	) : null;

	return { withSignature, refusalOf, dialog };
}
